/**
 * useCapgoUpdate — manual OTA update cycle for Capgo live updates.
 *
 * Why manual (autoUpdate: false in capacitor.config.ts):
 *   With autoUpdate:true, Capgo applies a new bundle the moment the app
 *   returns from background — regardless of what page the user is on.
 *   The WebView cold-reloads from /, and DashboardRouter redirects every
 *   user to /overview mid-session. That's a disruptive surprise on a
 *   monitoring app where operators are actively watching live data.
 *
 * Manual flow (this hook):
 *   1. On cold-start AND on every app-foreground: call checkAndDownload().
 *   2. getLatest() queries Capgo using the channel baked into
 *      capacitor.config.json (the "defaultChannel" field).
 *      cap:ios      → CAPGO_CHANNEL=development baked at cap sync time
 *      cap:ios:prod → CAPGO_CHANNEL=production  baked at cap sync time
 *   3. download() fetches the bundle silently in the background.
 *   4. downloadComplete fires → persistent "Update ready" toast appears.
 *   5. User taps "Restart now" → set() + reload() applies it.
 *
 * No-ops on web builds — isMobileBuild guard skips the dynamic import.
 *
 * IMPORTANT — call order:
 *   getLatest() must run first. current() is called ONLY inside its own
 *   nested try-catch after getLatest() returns a URL — it throws
 *   "already_on_builtin" on the native bundle, which means any available
 *   OTA bundle is newer, so we fall through to download.
 *   Do NOT call setChannel() — it is rate-limited by Capgo (429) and
 *   blocks all subsequent channel requests for the session when it fails.
 *   The channel baked into capacitor.config.json is sufficient.
 *
 * Why the localStorage guard exists (CAPGO_APPLIED_KEY):
 *   After set(bundle) + reload(), two Capgo API guards should prevent
 *   re-downloading: current().bundle.version === latest.version, and
 *   list() containing the bundle. Both fail in practice because:
 *   (a) Capgo deletes applied bundles from list() after notifyAppReady()
 *       commits them as "success" (disk-space cleanup), and
 *   (b) current() may still reflect pre-commit state on the first
 *       checkAndDownload() call after a fresh reload.
 *   Storing the applied version before reload() gives a guard that is
 *   independent of Capgo's internal state machine.
 */

import * as React from "react";
import { toast } from "sonner";
import { isMobileBuild } from "@/platform";
import { remoteLog } from "./remoteLog";

// Dual-write key — stored in BOTH Capacitor Preferences (NSUserDefaults /
// SharedPreferences) AND localStorage so the guard survives whichever store
// the OS preserves across a process kill.
//
// Why dual-write:
//   Android real devices: SharedPreferences.apply() is async-to-disk. If the
//   OS SIGKILLs the process between the apply() and the disk flush (common on
//   low-memory OEM devices), the Preferences value is lost on restart.
//   localStorage (WebView SQLite) commits synchronously at transaction close,
//   so it typically survives the same kill. Belt-and-suspenders: one of the
//   two stores will almost always carry the version through.
const CAPGO_APPLIED_KEY = "capgo_applied_version";


export function useCapgoUpdate(): void {
  React.useEffect(() => {
    if (!isMobileBuild) return;

    // Guard against the async import resolving after the component has
    // already unmounted. Without this, listeners would be registered but
    // the cleanup return below would have already run (cleanup still
    // undefined at that point), leaving dangling listeners.
    let cancelled = false;
    let cleanup: (() => void) | undefined;

    import("@capgo/capacitor-updater").then(async ({ CapacitorUpdater }) => {
      if (cancelled) return;

      const { Preferences } = await import("@capacitor/preferences");

      let isDownloading = false;

      // ── 1. Listen for download completion ──────────────────────────────
      const downloadHandle = await CapacitorUpdater.addListener(
        "downloadComplete",
        (info) => {
          isDownloading = false;
          const bundle = info.bundle;
          remoteLog("[Capgo] downloadComplete", { id: bundle.id, version: bundle.version });
          toast("Update ready", {
            description: "A new version has been downloaded.",
            duration: Infinity,
            id: "capgo-update-ready",
            action: {
              label: "Restart now",
              onClick: async () => {
                remoteLog("[Capgo] user tapped Restart", { id: bundle.id, version: bundle.version });

                // Write localStorage SYNCHRONOUSLY first — it's the fastest
                // guard available and doesn't need to await anything.
                try { localStorage.setItem(CAPGO_APPLIED_KEY, bundle.version); } catch { /* no-op */ }

                // AWAIT Preferences.set instead of fire-and-forget. On Android,
                // CapacitorUpdater.set() below triggers a native WebView reload
                // that can outrun an in-flight Preferences.set() promise —
                // SharedPreferences.apply() is async-to-disk and the WebView
                // teardown cancels the pending I/O. Awaiting blocks until the
                // bridge confirms the write was committed, so the version
                // survives the reload.
                try {
                  await Preferences.set({ key: CAPGO_APPLIED_KEY, value: bundle.version });
                  remoteLog("[Capgo] Preferences guard committed", { version: bundle.version });
                } catch (err) {
                  remoteLog("[Capgo] Preferences.set failed (localStorage still has it)", { error: String(err) });
                }

                remoteLog("[Capgo] guards stored, calling set()", { version: bundle.version });

                // Do NOT call reload() after set(). In Capgo v8.x, set()
                // calls _reload() internally on both iOS and Android.
                // The bridge-thread deadlock that historically blocked set()
                // for 30s on Android is fixed in @capgo/capacitor-updater
                // 8.45.11+ (PR #754, issue #753) — we now await safely.
                try {
                  await CapacitorUpdater.set(bundle);
                  remoteLog("[Capgo] set() resolved", { version: bundle.version });
                } catch (err) {
                  remoteLog("[Capgo] set() failed", { error: String(err), version: bundle.version });
                  void Preferences.remove({ key: CAPGO_APPLIED_KEY });
                  try { localStorage.removeItem(CAPGO_APPLIED_KEY); } catch { /* no-op */ }
                  toast.error("Couldn't apply update", {
                    description: "Please close and reopen the app.",
                  });
                }
              },
            },
          });
        },
      );

      // ── 2. Check + download helper ──────────────────────────────────────
      async function checkAndDownload(): Promise<void> {
        if (isDownloading) return;
        remoteLog("[Capgo] checkAndDownload: start");
        try {
          const latest = await CapacitorUpdater.getLatest();
          remoteLog("[Capgo] getLatest", { version: latest.version, hasUrl: !!latest.url });

          // Skip if Capgo returned no URL or no version string — a missing
          // version would make "unknown" === current.bundle.version always
          // false, causing a re-download on every foreground event.
          if (!latest.url || !latest.version) {
            remoteLog("[Capgo] skip: no url/version from server");
            return;
          }

          // Primary guard against the repeat-toast loop: if the user already
          // applied this exact version in the current install lifecycle,
          // skip. This is reliable even when Capgo's own current() /
          // list() state hasn't settled after a fresh reload().
          //
          // Dual-read: Preferences may be lost if Android SIGKILLed the
          // process before SharedPreferences.apply() flushed. Fall back to
          // localStorage which uses WebView SQLite (synchronous commit) and
          // typically survives the same kill. Use whichever value is non-null.
          const { value: prefsVersion } = await Preferences.get({ key: CAPGO_APPLIED_KEY });
          const lsVersion = (() => { try { return localStorage.getItem(CAPGO_APPLIED_KEY); } catch { return null; } })();
          const appliedVersion = prefsVersion ?? lsVersion;
          remoteLog("[Capgo] guard", { prefsVersion, lsVersion, appliedVersion, latestVersion: latest.version, match: appliedVersion === latest.version });
          if (appliedVersion === latest.version) {
            remoteLog("[Capgo] skip: guard matched", { prefsVersion, lsVersion });
            return;
          }

          // Avoid re-downloading a bundle already active or already staged.
          // current() throws "already_on_builtin" on the native bundle —
          // that means any available OTA bundle is newer, so fall through.
          try {
            const current = await CapacitorUpdater.current();
            remoteLog("[Capgo] current()", { version: current.bundle.version, status: current.bundle.status });
            if (current.bundle.version === latest.version) {
              remoteLog("[Capgo] skip: current bundle matches latest");
              return;
            }
          } catch (e) {
            remoteLog("[Capgo] current() threw (native bundle active)", { error: String(e) });
          }

          // Also check the on-device bundle list so we don't re-download a
          // bundle that was already fetched but not yet applied (user ignored
          // the toast, backgrounded, then foregrounded again).
          try {
            const { bundles } = await CapacitorUpdater.list();
            remoteLog("[Capgo] list()", { bundles: bundles.map((b) => ({ version: b.version, status: b.status })) });
            const alreadyStaged = bundles.some(
              (b) => b.version === latest.version,
            );
            if (alreadyStaged) {
              remoteLog("[Capgo] skip: already staged in list()");
              return;
            }
          } catch (e) {
            remoteLog("[Capgo] list() threw (non-fatal)", { error: String(e) });
          }

          // Snapshot network state before download so failures can be
          // correlated with connection type (cellular vs wifi) in BetterStack.
          let networkInfo: { connected: boolean; connectionType: string } | null = null;
          try {
            const { Network } = await import("@capacitor/network");
            const status = await Network.getStatus();
            networkInfo = { connected: status.connected, connectionType: status.connectionType };
          } catch {
            /* non-fatal — Network plugin unavailable in some build flavours */
          }

          remoteLog("[Capgo] ALL guards passed — starting download", {
            version: latest.version,
            network: networkInfo,
          });
          isDownloading = true;
          await CapacitorUpdater.download({
            url: latest.url,
            version: latest.version,
          });
        } catch (e) {
          const msg = String(e);
          isDownloading = false;

          // "no_new_version_available" is Capgo's normal "nothing to update"
          // response — not an actual error. Log it at debug level so real
          // errors don't get drowned out in the dashboard.
          if (msg.includes("no_new_version_available")) {
            remoteLog("[Capgo] no update available");
            return;
          }

          // On failure, re-read network state — iOS silently kills downloads
          // over cellular when Low Data Mode is on or the app's cellular
          // access is disabled in Settings → Cellular → <app>.
          let networkOnError: { connected: boolean; connectionType: string } | null = null;
          try {
            const { Network } = await import("@capacitor/network");
            const status = await Network.getStatus();
            networkOnError = { connected: status.connected, connectionType: status.connectionType };
          } catch {
            /* non-fatal */
          }

          remoteLog("[Capgo] checkAndDownload error", {
            error: msg,
            network: networkOnError,
            hint: networkOnError?.connectionType === "cellular"
              ? "iOS may be blocking download over cellular — check Settings → Cellular → app toggle or Low Data Mode"
              : networkOnError?.connected === false
                ? "device appears offline at time of failure"
                : "non-network error",
          });
        }
      }

      // ── 3. Check on cold-start ──────────────────────────────────────────
      void checkAndDownload();

      // ── 4. Check on every app foreground ───────────────────────────────
      const { App: CapApp } = await import("@capacitor/app");
      const stateHandle = await CapApp.addListener(
        "appStateChange",
        ({ isActive }) => {
          if (isActive) void checkAndDownload();
        },
      );

      cleanup = () => {
        void downloadHandle.remove();
        void stateHandle.remove();
      };
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);
}
